import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import { Button, Badge, Icon, Card } from '../components/ui.js';

function AboutScreen() {
    const s = useStore();
    const t = useTheme(s.theme);

    // What We Offer Data
    const offerings = [
        { icon: 'briefcase', label: 'Jobs', desc: 'Find your dream job from top companies', color: '#3B82F6' },
        { icon: 'cap', label: 'Internships', desc: 'Gain real-world experience', color: '#8B5CF6' },
        { icon: 'building', label: 'Admissions', desc: 'Apply to top universities worldwide', color: '#14B8A6' },
        { icon: 'award', label: 'Scholarships', desc: 'Fund your education with ease', color: '#22C55E' },
        { icon: 'spark', label: 'Events', desc: 'Attend webinars & career fairs', color: '#F97316' },
    ];

    // Why Choose Us Features
    const features = [
        { icon: 'grid', title: 'All in One Place', desc: 'Jobs, internships, scholarships, admissions, and events — everything you need' },
        { icon: 'shieldCheck', title: 'Verified Listings', desc: 'Every opportunity is reviewed and verified by our team' },
        { icon: 'arrowUpRight', title: 'Easy Application', desc: 'Apply with one click — internal and external applications supported' },
        { icon: 'smartphone', title: 'Responsive Design', desc: 'Access from any device — desktop, tablet, or mobile' },
    ];

    return React.createElement('div', { style: { width: '100%' } },
        // ========================================
        // 1. HERO BANNER
        // ========================================
        React.createElement('div', {
            style: {
                background: s.theme === 'dark' ? OC.charcoal : '#F9FAFB',
                borderBottom: `1px solid ${t.border.base}`,
                padding: '80px 0 60px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }
        },
            // Background decoration
            React.createElement('div', {
                style: {
                    position: 'absolute',
                    top: '-200px',
                    right: '-100px',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: OC.orange + '08',
                    pointerEvents: 'none'
                }
            }),
            React.createElement('div', {
                style: {
                    position: 'absolute',
                    bottom: '-100px',
                    left: '-50px',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: OC.orange + '06',
                    pointerEvents: 'none'
                }
            }),
            
            React.createElement('div', { style: { maxWidth: 900, margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 1 } },
                React.createElement(Badge, {
                    color: OC.orange,
                    theme: s.theme,
                    style: { fontSize: 13, padding: '6px 16px', marginBottom: 16 }
                }, '🚀 Your Gateway to Success'),
                
                React.createElement('h1', {
                    style: {
                        fontFamily: FONT.display,
                        fontWeight: 800,
                        fontSize: 52,
                        color: t.text.primary,
                        margin: '0 0 16px',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1
                    }
                },
                    'Your Gateway to',
                    React.createElement('br', null),
                    React.createElement('span', { style: { color: OC.orange } }, 'Career & Education Opportunities')
                ),
                
                React.createElement('p', {
                    style: {
                        fontSize: 18,
                        color: t.text.secondary,
                        lineHeight: 1.7,
                        maxWidth: 640,
                        margin: '0 auto 32px'
                    }
                },
                    'Discover thousands of verified opportunities — jobs, internships, scholarships, admissions, and events — all in one place.'
                ),
                
                React.createElement('div', { style: { display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' } },
                    React.createElement(Button, {
                        kind: 'primary',
                        size: 'lg',
                        theme: s.theme,
                        iconRight: React.createElement(Icon, { name: 'arrowRight', size: 16 }),
                        onClick: () => s.go('feed')
                    }, 'Get Started'),
                    React.createElement(Button, {
                        kind: 'secondary',
                        size: 'lg',
                        theme: s.theme,
                        onClick: () => s.go('signup')
                    }, 'Join Now')
                )
            )
        ),

        // ========================================
        // 2. ABOUT US
        // ========================================
        React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '60px 32px' } },
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' } },
                // Left: Content
                React.createElement('div', null,
                    React.createElement(Badge, {
                        color: OC.orange,
                        theme: s.theme,
                        style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }
                    }, 'About Us'),
                    React.createElement('h2', {
                        style: {
                            fontFamily: FONT.display,
                            fontWeight: 800,
                            fontSize: 34,
                            color: t.text.primary,
                            margin: '12px 0 16px',
                            letterSpacing: '-0.02em'
                        }
                    }, 'Connecting Talent with Opportunity'),
                    React.createElement('p', {
                        style: {
                            fontSize: 16,
                            color: t.text.secondary,
                            lineHeight: 1.8,
                            margin: '0 0 16px'
                        }
                    },
                        'We are a comprehensive career platform designed for ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'students'),
                        ', ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'fresh graduates'),
                        ', and ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'professionals'),
                        ' — connecting you with verified opportunities from around the world.'
                    ),
                    React.createElement('p', {
                        style: {
                            fontSize: 16,
                            color: t.text.secondary,
                            lineHeight: 1.8,
                            margin: 0
                        }
                    },
                        'From ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'job listings'),
                        ' and ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'internships'),
                        ' to ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'scholarships'),
                        ' and ',
                        React.createElement('strong', { style: { color: t.text.primary } }, 'university admissions'),
                        ' — we provide trusted opportunities in one seamless platform.'
                    )
                ),
                // Right: Stats/Image placeholder
                React.createElement('div', {
                    style: {
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 16
                    }
                },
                    [
                        { value: '10k+', label: 'Live Opportunities' },
                        { value: '500+', label: 'Trusted Employers' },
                        { value: '50+', label: 'Countries' },
                        { value: '100%', label: 'Free Access' }
                    ].map(stat =>
                        React.createElement(Card, {
                            key: stat.label,
                            theme: s.theme,
                            padding: 20,
                            style: { textAlign: 'center' }
                        },
                            React.createElement('div', {
                                style: {
                                    fontFamily: FONT.display,
                                    fontWeight: 800,
                                    fontSize: 28,
                                    color: OC.orange
                                }
                            }, stat.value),
                            React.createElement('div', {
                                style: {
                                    fontSize: 13,
                                    color: t.text.secondary,
                                    marginTop: 4
                                }
                            }, stat.label)
                        )
                    )
                )
            )
        ),

        // ========================================
        // 3. OUR MISSION
        // ========================================
        React.createElement('div', {
            style: {
                background: t.bg.card,
                borderTop: `1px solid ${t.border.base}`,
                borderBottom: `1px solid ${t.border.base}`,
                padding: '60px 32px'
            }
        },
            React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', textAlign: 'center' } },
                React.createElement(Badge, {
                    color: OC.orange,
                    theme: s.theme,
                    style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }
                }, 'Our Mission'),
                
                React.createElement('h2', {
                    style: {
                        fontFamily: FONT.display,
                        fontWeight: 800,
                        fontSize: 34,
                        color: t.text.primary,
                        margin: '12px 0 16px',
                        letterSpacing: '-0.02em'
                    }
                }, 'Making Career Opportunities Accessible to Everyone'),
                
                React.createElement('p', {
                    style: {
                        fontSize: 17,
                        color: t.text.secondary,
                        lineHeight: 1.8,
                        maxWidth: 700,
                        margin: '0 auto 40px'
                    }
                },
                    'We believe in breaking down barriers. Our mission is to connect students and employers with trusted, verified opportunities — all on a single, easy-to-use platform.'
                ),
                
                // Mission Points
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, textAlign: 'left' } },
                    [
                        { icon: 'globe', title: 'Accessible Opportunities', desc: 'Career opportunities made available to everyone, everywhere' },
                        { icon: 'users', title: 'Connect Talent & Employers', desc: 'Bridging the gap between job seekers and companies' },
                        { icon: 'shieldCheck', title: 'Trusted & Verified', desc: 'Every listing is reviewed for authenticity and quality' }
                    ].map(item =>
                        React.createElement(Card, {
                            key: item.title,
                            theme: s.theme,
                            padding: 24,
                            style: { display: 'flex', gap: 14, alignItems: 'flex-start' }
                        },
                            React.createElement('div', {
                                style: {
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: s.theme === 'dark' ? 'rgba(249,115,22,.12)' : OC.orangeMuted,
                                    color: OC.orange,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: '0 0 auto'
                                }
                            }, React.createElement(Icon, { name: item.icon, size: 20 })),
                            React.createElement('div', null,
                                React.createElement('h4', {
                                    style: {
                                        fontFamily: FONT.display,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: t.text.primary,
                                        margin: '0 0 4px'
                                    }
                                }, item.title),
                                React.createElement('p', {
                                    style: {
                                        fontSize: 13,
                                        color: t.text.secondary,
                                        margin: 0,
                                        lineHeight: 1.5
                                    }
                                }, item.desc)
                            )
                        )
                    )
                )
            )
        ),

        // ========================================
        // 4. WHAT WE OFFER (5 Cards)
        // ========================================
        React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '60px 32px' } },
            React.createElement('div', { style: { textAlign: 'center', marginBottom: 40 } },
                React.createElement(Badge, {
                    color: OC.orange,
                    theme: s.theme,
                    style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }
                }, 'What We Offer'),
                React.createElement('h2', {
                    style: {
                        fontFamily: FONT.display,
                        fontWeight: 800,
                        fontSize: 34,
                        color: t.text.primary,
                        margin: '12px 0 8px',
                        letterSpacing: '-0.02em'
                    }
                }, 'Explore Opportunities Across All Categories'),
                React.createElement('p', {
                    style: {
                        fontSize: 16,
                        color: t.text.secondary,
                        margin: 0
                    }
                }, 'Find exactly what you\'re looking for')
            ),
            
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 } },
                offerings.map(o =>
                    React.createElement(Card, {
                        key: o.label,
                        theme: s.theme,
                        padding: 24,
                        style: {
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all .2s ease',
                            border: `1px solid ${t.border.base}`
                        },
                        onMouseEnter: e => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = t.shadow.md;
                        },
                        onMouseLeave: e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        },
                        onClick: () => {
                            const catMap = {
                                'Jobs': 'jobs',
                                'Internships': 'internships',
                                'Admissions': 'admissions',
                                'Scholarships': 'scholarships',
                                'Events': 'events'
                            };
                            s.go('feed', { cat: catMap[o.label] || 'all' });
                        }
                    },
                        React.createElement('div', {
                            style: {
                                width: 56,
                                height: 56,
                                borderRadius: 14,
                                background: s.theme === 'dark' ? `${o.color}26` : `${o.color}14`,
                                color: o.color,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 12
                            }
                        }, React.createElement(Icon, { name: o.icon, size: 26 })),
                        React.createElement('h4', {
                            style: {
                                fontFamily: FONT.display,
                                fontWeight: 700,
                                fontSize: 17,
                                color: t.text.primary,
                                margin: '0 0 4px'
                            }
                        }, o.label),
                        React.createElement('p', {
                            style: {
                                fontSize: 13,
                                color: t.text.secondary,
                                margin: 0,
                                lineHeight: 1.4
                            }
                        }, o.desc)
                    )
                )
            )
        ),

        // ========================================
        // 5. WHY CHOOSE US (Features)
        // ========================================
        React.createElement('div', {
            style: {
                background: t.bg.card,
                borderTop: `1px solid ${t.border.base}`,
                borderBottom: `1px solid ${t.border.base}`,
                padding: '60px 32px'
            }
        },
            React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto' } },
                React.createElement('div', { style: { textAlign: 'center', marginBottom: 40 } },
                    React.createElement(Badge, {
                        color: OC.orange,
                        theme: s.theme,
                        style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }
                    }, 'Why Choose Us'),
                    React.createElement('h2', {
                        style: {
                            fontFamily: FONT.display,
                            fontWeight: 800,
                            fontSize: 34,
                            color: t.text.primary,
                            margin: '12px 0 8px',
                            letterSpacing: '-0.02em'
                        }
                    }, 'Built for Your Success'),
                    React.createElement('p', {
                        style: {
                            fontSize: 16,
                            color: t.text.secondary,
                            margin: 0
                        }
                    }, 'Everything you need in one place')
                ),
                
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 } },
                    features.map(f =>
                        React.createElement(Card, {
                            key: f.title,
                            theme: s.theme,
                            padding: 24,
                            style: { textAlign: 'center' }
                        },
                            React.createElement('div', {
                                style: {
                                    width: 52,
                                    height: 52,
                                    borderRadius: 12,
                                    background: s.theme === 'dark' ? 'rgba(249,115,22,.12)' : OC.orangeMuted,
                                    color: OC.orange,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: 12
                                }
                            }, React.createElement(Icon, { name: f.icon, size: 24 })),
                            React.createElement('h4', {
                                style: {
                                    fontFamily: FONT.display,
                                    fontWeight: 700,
                                    fontSize: 16,
                                    color: t.text.primary,
                                    margin: '0 0 4px'
                                }
                            }, f.title),
                            React.createElement('p', {
                                style: {
                                    fontSize: 13,
                                    color: t.text.secondary,
                                    margin: 0,
                                    lineHeight: 1.5
                                }
                            }, f.desc)
                        )
                    )
                )
            )
        ),

        // ========================================
        // 6. OUR VISION
        // ========================================
        React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '60px 32px' } },
            React.createElement('div', {
                style: {
                    background: s.theme === 'dark' ? 'rgba(249,115,22,.06)' : OC.orangeMuted,
                    borderRadius: 20,
                    padding: '48px 40px',
                    textAlign: 'center'
                }
            },
                React.createElement(Badge, {
                    color: OC.orange,
                    theme: s.theme,
                    style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }
                }, 'Our Vision'),
                
                React.createElement('h2', {
                    style: {
                        fontFamily: FONT.display,
                        fontWeight: 800,
                        fontSize: 34,
                        color: t.text.primary,
                        margin: '12px 0 16px',
                        letterSpacing: '-0.02em'
                    }
                }, 'Shaping the Future of Careers'),
                
                React.createElement('p', {
                    style: {
                        fontSize: 17,
                        color: t.text.secondary,
                        lineHeight: 1.8,
                        maxWidth: 700,
                        margin: '0 auto'
                    }
                },
                    'To become the ',
                    React.createElement('strong', { style: { color: OC.orange } }, 'leading career platform'),
                    ' for students and professionals, and to create a ',
                    React.createElement('strong', { style: { color: OC.orange } }, 'strong connection'),
                    ' between employers and top talent — building a future where opportunities are accessible to all.'
                )
            )
        ),

        // ========================================
        // 7. CALL TO ACTION
        // ========================================
        React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '0 32px 80px' } },
            React.createElement('div', {
                style: {
                    background: OC.orange,
                    borderRadius: 20,
                    padding: '56px 40px',
                    textAlign: 'center',
                    color: '#fff'
                }
            },
                React.createElement('h2', {
                    style: {
                        fontFamily: FONT.display,
                        fontWeight: 800,
                        fontSize: 36,
                        margin: '0 0 12px',
                        letterSpacing: '-0.02em'
                    }
                }, 'Start Your Career Journey Today'),
                
                React.createElement('p', {
                    style: {
                        fontSize: 16,
                        opacity: 0.9,
                        maxWidth: 540,
                        margin: '0 auto 28px',
                        lineHeight: 1.6
                    }
                }, 'Join thousands of students and professionals finding their dream opportunities'),
                
                React.createElement('div', {
                    style: {
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }
                },
                    [
                        { label: 'Browse Jobs', cat: 'jobs' },
                        { label: 'Explore Internships', cat: 'internships' },
                        { label: 'Discover Scholarships', cat: 'scholarships' },
                        { label: 'Find Admissions', cat: 'admissions' },
                        { label: 'Join Events', cat: 'events' },
                    ].map(btn =>
                        React.createElement(Button, {
                            key: btn.label,
                            kind: 'ghost',
                            size: 'md',
                            style: {
                                background: 'rgba(255,255,255,.15)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,.2)',
                                fontWeight: 600
                            },
                            onClick: () => s.go('feed', { cat: btn.cat })
                        }, btn.label)
                    )
                )
            )
        )
    );
}

export { AboutScreen };
